import { useSearch } from "@tanstack/react-router";
import { useGetOutgoingPostbackQuery } from "api/TenantSlug/OutgoingPostback/hooks";
import { ModifyPostbackOutgoingDrawer } from "components/Drawers/ModifyPostbackOutgoingDrawer/ModifyPostbackOutgoingDrawer.tsx";
import { TrackingToolToGeneralForm } from "components/Forms/TrackingToolToGeneralForm/TrackingToolToGeneralForm.tsx";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import { PermissionEnum } from "types/enums.ts";
import { DrawerId, FormId } from "types/models.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { getTrackingToolToItems } from "utils/tenantHeaderItems.ts";

interface SearchParams {
    [`modifyOutgoingPostback-id`]: string;
}

export const Route = createTenantFileRoute(
    "/_authenticated/$tenantSlug/_layout/tool/_layout/tracking/_layout/tracking-api/_layout/$id/_layout/general/"
)({
    component: TrackingToolToGeneralPage,
    staticData: {
        permissions: [PermissionEnum.ToolTrackingOutgoingEditGeneral],
        showInSideMenu: false,
        label: "pages.tool.tracking.to.general.title",
    },
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.tool.tracking.to.general.title"),
        };
    },
});

function TrackingToolToGeneralPage() {
    const { id } = Route.useParams();
    const { data, isLoading, isError } = useGetOutgoingPostbackQuery(id, {
        filter: {
            include:
                "types,sources,country,offers,excludedOffers,affiliates,advertisers,integrations,cascades,formStructures",
        },
    });
    const outgoingPostback = data?.outgoingPostback;
    const formId = FormId.TrackingToolToGeneralForm;
    const drawerId = DrawerId.ModifyOutgoingPostback;
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const permissionsUtils = usePermission();

    if (!outgoingPostback) {
        return (
            <QueryState
                isLoading={isLoading}
                isError={isError || !isLoading}
                errorKey="errors.loading_outgoing_postback"
            />
        );
    }

    return (
        <TenantPage
            headerNavigationItems={() => getTrackingToolToItems()}
            isEdit
            saveFormId={permissionsUtils.hasPermission(PermissionEnum.ToolTrackingOutgoingEditGeneral) ? formId : null}
        >
            <TrackingToolToGeneralForm key={id} postback={outgoingPostback} />
            {searchParams[`${drawerId}-id`] &&
                permissionsUtils.hasPermission(PermissionEnum.ToolTrackingOutgoingPostbackExportView) && (
                    <ModifyPostbackOutgoingDrawer />
                )}
        </TenantPage>
    );
}
